import { Bounds, Vector } from '@ardoq/graph';
import { rectCenter, rectHeight, rectWidth } from './geometry';
import { easeOutCubic, interp } from './math';
import { currentTimestamp } from '@ardoq/date-time';

const TRANSITION_DURATION = 500;
const TRANSITION_TWEEN = easeOutCubic;

export type Projection = {
  adjustToViewport: (vp: Bounds, dpr: number) => void;
  adjustToRect: (wd: Bounds, margin: number, doAnimate: boolean) => void;
  zoomToPoint: (wd: Vector, scale: number, doAnimate: boolean) => void;
  adjustToPoint: (wd: Vector, vp: Vector, doAnimate: boolean) => void;
  translateBy: (wd: Vector, doAnimate: boolean) => void;

  toViewport: (wd: Vector) => Vector; // this seems to be used to convert world to both device and viewport
  toWorld: (wd: Vector) => Vector; // this seems to only be used to convert events (device) to world

  viewport: () => Bounds;
  window: (animated: boolean) => Bounds;
  matrix: () => DOMMatrix;
  scale: (animated: boolean) => number;
  resolution: (animated: boolean) => number;

  isAnimating: () => boolean;
};

type Parameters = {
  scale: number;
  translation: Vector;
};

export const createProjection = (): Projection => {
  let viewport: Bounds = [0, 0, 1, 1];
  let dpr = 1;

  let animationBegin = NaN;
  let scaleBegin: number = NaN;
  let translationBegin: Vector = [NaN, NaN];
  let scale: number = 1;
  let translation: Vector = [0, 0];

  /**
   * set the viewport. The projection will adjust itself as best it can
   * @param rect the viewport rectangle.
   * @param _res (UNUSED) the viewport resolution (likely determined by window.devicePixelRatio)
   */
  const adjustToViewport = (rect: Bounds, _res: number = 1) => {
    dpr = 1;
    viewport = [rect[0] * dpr, rect[1] * dpr, rect[2] * dpr, rect[3] * dpr];
  };

  // adjust projection to best fit specified world rect
  const adjustToRect = (wd: Bounds, margin: number, doAnimate: boolean) => {
    const vp = viewport;
    const wdSize = [rectWidth(wd), rectHeight(wd)];
    const vpSize = [rectWidth(vp) - 2 * margin, rectHeight(vp) - 2 * margin];
    const cwd = rectCenter(wd);
    const cvp = rectCenter(vp);

    // create new paramaters
    const scale = Math.min(vpSize[0] / wdSize[0], vpSize[1] / wdSize[1]);
    const translation: Vector = [
      cvp[0] - cwd[0] * scale,
      cvp[1] - cwd[1] * scale,
    ];

    setParameters({ scale: scale, translation: translation }, doAnimate);
  };

  const zoomToPoint = (center: Vector, value: number, doAnimate: boolean) => {
    const vp = toViewport(center);

    setParameters(
      {
        scale: value,
        translation: [vp[0] - center[0] * value, vp[1] - center[1] * value],
      },
      doAnimate
    );
  };

  // adjust projection to put the specified world point at the specified viewport point
  const adjustToPoint = (wd: Vector, vp: Vector, doAnimate: boolean) => {
    const { scale } = getParameters(false);

    setParameters(
      {
        scale: scale,
        translation: [vp[0] - wd[0] * scale, vp[1] - wd[1] * scale],
      },
      doAnimate
    );
  };

  // pan the projection by the specified amount. do not like
  const translateBy = (wd: Vector, doAnimate: boolean) => {
    const { scale, translation: translation } = getParameters(false);

    setParameters(
      {
        scale: scale,
        translation: [
          translation[0] + wd[0] * scale,
          translation[1] + wd[1] * scale,
        ],
      },
      doAnimate
    );
  };

  /* convert a world point to viewport coordinates */
  const toViewport = (wd: Vector): Vector => {
    const { scale, translation: translation } = getParameters(false);

    return [wd[0] * scale + translation[0], wd[1] * scale + translation[1]];
  };

  /* convert a device point to world coordinates */
  const toWorld = (vp: Vector): Vector => {
    const { scale, translation: translation } = getParameters(false);

    return [
      (vp[0] * dpr - translation[0]) / scale,
      (vp[1] * dpr - translation[1]) / scale,
    ];
  };

  /* get the derived window rectangle in world coordinates */
  const window = (animated: boolean): Bounds => {
    const { scale, translation: translation } = getParameters(animated);

    return [
      (viewport[0] - translation[0]) / scale,
      (viewport[1] - translation[1]) / scale,
      (viewport[2] - translation[0]) / scale,
      (viewport[3] - translation[1]) / scale,
    ];
  };

  const matrix = () => {
    const { scale, translation: translation } = getParameters(true);

    return new DOMMatrix([scale, 0, 0, scale, translation[0], translation[1]]);
  };

  const isAnimating = () => !isNaN(animationBegin);

  const setParameters = (parameters: Parameters, doAnimate: boolean) => {
    const canAnimate = !(
      isNaN(scaleBegin) ||
      isNaN(translationBegin[0]) ||
      isNaN(translationBegin[1])
    );

    if (doAnimate && canAnimate) {
      if (isAnimating()) {
        const begin = getParameters(true);

        scaleBegin = begin.scale;
        translationBegin = begin.translation;
      } else {
        /* start an animation */
        animationBegin = currentTimestamp();
        scaleBegin = scale;
        translationBegin = translation;
      }
    } else {
      animationBegin = NaN;
      scaleBegin = parameters.scale;
      translationBegin = parameters.translation;
    }

    scale = parameters.scale;
    translation = parameters.translation;
  };

  const getParameters = (animated: boolean): Parameters => {
    const canAnimate = !(
      isNaN(scaleBegin) ||
      isNaN(translationBegin[0]) ||
      isNaN(translationBegin[1])
    );

    if (!animated || !canAnimate) {
      return {
        scale: scale,
        translation: translation,
      };
    }

    const p = isNaN(animationBegin)
      ? 1.0
      : (currentTimestamp() - animationBegin) / TRANSITION_DURATION;

    if (p >= 1.0) {
      animationBegin = NaN;

      return {
        scale: scale,
        translation: translation,
      };
    }

    const q = TRANSITION_TWEEN(p);

    return {
      scale: interp(q, scaleBegin, scale),
      translation: [
        interp(q, translationBegin[0], translation[0]),
        interp(q, translationBegin[1], translation[1]),
      ],
    };
  };

  return {
    adjustToViewport, // adjust the transform to best fit itself to the specified viewport
    adjustToRect, // adjust the transform to best fit itself to the current viewport
    adjustToPoint, // adjust the translation to place the specified world position at the specified viewport position
    zoomToPoint, // adjust the scale around the specified world position
    translateBy,
    matrix, // get a DOMMatrix which represents the projection
    window, // get the derived window rectangle in world coordinates
    viewport: () => viewport, // get the device rectangle in device coordinates
    scale: (animated = true) => getParameters(animated).scale, // get the scale
    resolution: (animated = true) => 1.0 / getParameters(animated).scale, // get the scale

    toViewport: toViewport,
    toWorld: toWorld,

    isAnimating: isAnimating,
  };
};
